// Pure, isomorphic import logic for the "Import GG PDF" feature. NO firebase,
// NO react, NO browser/node-only APIs — so the SAME code runs in the admin UI
// and in the headless proof harness. Given the parsed teams from the
// parseGgPdf Cloud Function and the qualifying event's existing teams, it
// decides what to ADD, what to SKIP (already there), and what to FLAG (a
// scorecard that wouldn't reconcile, or a score that disagrees with what's
// already stored). It never mutates anything — the caller writes on approve.

const SUFFIX = new Set(['ii', 'iii', 'iv', 'jr', 'sr'])

// Last name of a stored player string ("First Last" / "First Last II").
export function lastNameOf(name) {
  const toks = String(name || '')
    .replace(/[.,]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .filter((t) => !SUFFIX.has(t.toLowerCase()))
  return (toks[toks.length - 1] || '').toLowerCase()
}

// Order-independent identity key for a team = its two last names, sorted.
export function pairKey(lastA, lastB) {
  return [String(lastA || '').toLowerCase(), String(lastB || '').toLowerCase()].sort().join('|')
}

// Map an existing teams object → { pairKey: { id, team } } for fast lookup.
// Every team with two players participates; blanks still key (empty last name).
export function indexExistingTeams(teamsMap) {
  const idx = new Map()
  for (const [id, t] of Object.entries(teamsMap || {})) {
    if (!t) continue
    const key = pairKey(lastNameOf(t.player1), lastNameOf(t.player2))
    if (!idx.has(key)) idx.set(key, { id, team: t })
  }
  return idx
}

// Build the exact stored team record — matches the existing qualified teams
// (player1, player2, teamName, score, holes[null,1..18], status:'submitted',
// approved:false, createdAt, submittedAt, accessToken). `token` + `now` are
// injected by the caller so this stays pure/isomorphic. An NS (no-score) roster
// team on the PDF but missing from the field is added UNSCORED (no score/holes/
// submittedAt, status:'pending') so the field mirrors the PDF exactly.
export function buildTeamRecord(team, token, now) {
  const rec = {
    player1: team.player1,
    player2: team.player2,
    teamName: team.teamName,
    status: team.ns ? 'pending' : 'submitted',
    approved: false,
    createdAt: now,
    accessToken: token,
  }
  if (!team.ns && team.total != null) {
    rec.score = team.total
    rec.holes = team.holes // [null, h1..h18]
    rec.submittedAt = now
  }
  return rec
}

// Categorize the FULL PDF field (scored `parsedTeams` + `nsTeams` roster) against
// the existing field to make it an EXACT MIRROR of the PDF — nothing more, nothing
// less. Match is by surname pair (suffix-aware), order-independent.
//   adding:   on the PDF, not in the field → written (scored w/ holes, or NS unscored)
//   updating: in the field but score/holes differ from the PDF (incl. field-NS → PDF-scored)
//   skipping: in the field and already matches the PDF (unchanged)
//   flagged:  a scored card that wouldn't reconcile (never auto-written)
//   removing: in the field but NOT on the PDF → deleted to reach the exact field
// `pdfTotal` is the PDF's team count (scored + NS) so the UI/proof can confirm it
// (e.g. 104) before approving a destructive mirror. Returns plain arrays.
export function categorizeImport(parsedTeams, existingTeamsMap, nsTeams = []) {
  const idx = indexExistingTeams(existingTeamsMap)
  const adding = []
  const updating = []
  const skipping = []
  const flagged = []

  const keyOf = (t) => pairKey(t.lastNames?.[0] ?? lastNameOf(t.player1), t.lastNames?.[1] ?? lastNameOf(t.player2))
  // PDF roster as a multiset of surname-pair keys — the mirror target.
  const pdfCount = new Map()
  for (const t of [...(parsedTeams || []), ...(nsTeams || [])]) {
    const k = keyOf(t)
    pdfCount.set(k, (pdfCount.get(k) || 0) + 1)
  }

  // Scored PDF rows → add / update / skip / flag.
  for (const t of parsedTeams || []) {
    const existing = idx.get(keyOf(t))
    if (existing) {
      const stored = Number(existing.team.score)
      const pdf = Number(t.total ?? t.gross)
      const storedIsScore = Number.isFinite(stored)
      if (storedIsScore && Number.isFinite(pdf) && stored !== pdf) {
        // Field score disagrees with the club's PDF (source of truth). Bring it
        // in line (score + hole-by-hole) IF the card verified; else flag.
        if (t.verified) updating.push({ ...t, existingId: existing.id, oldScore: stored, newScore: pdf })
        else flagged.push({ ...t, existingId: existing.id, reason: `already in as "${existing.team.teamName}" (${stored}) but PDF says ${pdf}, and its scorecard wouldn't read (${t.error || 'unverified'}) — left unchanged` })
      } else if (!storedIsScore && Number.isFinite(pdf) && t.verified) {
        // Field team is UNSCORED but the PDF now has a score → bring it in line.
        updating.push({ ...t, existingId: existing.id, oldScore: null, newScore: pdf })
      } else {
        skipping.push({ ...t, existingId: existing.id })
      }
      continue
    }
    if (!t.verified) { flagged.push({ ...t, reason: t.error || 'could not verify scorecard' }); continue }
    adding.push(t)
  }

  // NS (no-score) PDF rows → add if missing (unscored), else keep (skip). NS teams
  // never "update" — there is no score to reconcile.
  for (const t of nsTeams || []) {
    const existing = idx.get(keyOf(t))
    if (existing) skipping.push({ ...t, existingId: existing.id, ns: true })
    else adding.push({ ...t, ns: true })
  }

  // REMOVING — every field team beyond the PDF's multiset for its surname pair.
  // A field team keeps its slot only while the PDF still has an unconsumed match;
  // any extra (stray, duplicate, or simply not on the PDF) is removed.
  const removing = []
  const remain = new Map(pdfCount)
  for (const [id, team] of Object.entries(existingTeamsMap || {})) {
    if (!team) continue
    const k = pairKey(lastNameOf(team.player1), lastNameOf(team.player2))
    const have = remain.get(k) || 0
    if (have > 0) remain.set(k, have - 1)
    else removing.push({ existingId: id, teamName: team.teamName, player1: team.player1, player2: team.player2, score: team.score ?? null })
  }

  const pdfTotal = (parsedTeams || []).length + (nsTeams || []).length
  return { adding, updating, skipping, flagged, removing, pdfTotal }
}
